'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { trackEvent } from '@/lib/track';
import {
  StyledBrandMarkStrip,
  StyledBrandMarkControl,
  StyledBrandMarkLine,
} from './brandMark.styles';

/**
 * The small "Made with commoninja.com/<path>" credit on free-tool exports.
 *
 * WHY. Research of 2026-10-08 (research/2026-10-08-free-tool-branding/) found a
 * small, visible credit on shared outputs is the one free growth loop with real
 * evidence behind it, and that what users hate is a big mark or one that only
 * appears at download. So the credit is small, it is in the live preview from
 * the first second, and anyone can remove it for free with an account.
 *
 * TEST. Shipped on three tools first (fake Messenger, fake iMessage, tier
 * list) on 2026-10-08, with fake Telegram and fake Instagram DM left unbranded
 * as the comparison. Rolled out on 2026-10-12 to the rest of the "shareable"
 * image tools after an early read showed no drop in downloads.
 *
 * ⛔ NEVER add fake Telegram or fake Instagram DM while the /impact read
 * (free-tool-brand-mark, due ~2026-11-09) is open: they are the control group.
 * ⛔ WhatsApp waits for that read too (about 70% of free-tool clicks).
 * ⛔ Tools whose output becomes the user's OWN asset stay clean on purpose:
 * logo, favicon, QR code, business card, OG image, brand kit, YouTube
 * thumbnail, story maker, product mockup, gift certificate, thermometer, org
 * chart, resizer, grid splitter, carousel, screenshot beautifier.
 *
 * The credit prints the tool's real address, so it works for anyone who types
 * it, with no redirect to maintain (Sergei, 2026-10-08). The cost: a visit from
 * a typed credit cannot be told apart from any other direct visit, so the test
 * reads branded search and signups, not credit visits.
 */
export const BRAND_MARK_PATHS = {
  'fake-messenger-chat-generator': 'free-tools/social-media/fake-messenger-chat-generator',
  // Venn Diagram Maker, 2026-10-14: homework, slides and posts get shared.
  'venn-diagram-maker': 'free-tools/engagement/venn-diagram-maker',
  'fake-imessage-generator': 'free-tools/social-media/fake-imessage-generator',
  'tier-list-maker': 'free-tools/engagement/tier-list-maker',
  // Holiday countdown, 2026-10-14. The shared image, each page its own address.
  'holiday-countdown': 'free-tools/engagement/holiday-countdown',
  'holiday-countdown-christmas': 'free-tools/engagement/holiday-countdown/christmas',
  'holiday-countdown-new-years': 'free-tools/engagement/holiday-countdown/new-years',
  'holiday-countdown-black-friday': 'free-tools/engagement/holiday-countdown/black-friday',
  'holiday-countdown-hanukkah': 'free-tools/engagement/holiday-countdown/hanukkah',
  // Rollout 2026-10-12.
  'fake-snapchat-generator': 'free-tools/social-media/fake-snapchat-generator',
  'fake-instagram-post-generator': 'free-tools/social-media/fake-instagram-post-generator',
  'fake-facebook-post-generator': 'free-tools/social-media/fake-facebook-post-generator',
  'fake-ai-overview-generator': 'free-tools/social-media/fake-ai-overview-generator',
  'fake-chatgpt-generator': 'free-tools/social-media/fake-chatgpt-generator',
  'fake-gemini-generator': 'free-tools/social-media/fake-gemini-generator',
  'fake-reddit-post-generator': 'free-tools/social-media/fake-reddit-post-generator',
  'fake-tweet-generator': 'free-tools/social-media/fake-tweet-generator',
  'meme-generator': 'free-tools/social-media/meme-generator',
  // Bar Graph Maker, 2026-10-14 (Sergei: graphs for school, slides and posts get shared).
  'bar-graph-maker': 'free-tools/business/bar-graph-maker',
  'quote-card-maker': 'free-tools/social-media/quote-card-maker',
  'text-behind-image': 'free-tools/social-media/text-behind-image',
  'printable-bracket-maker': 'free-tools/engagement/printable-bracket-maker',
  '8-team-bracket': 'free-tools/engagement/8-team-bracket',
  '16-team-bracket': 'free-tools/engagement/16-team-bracket',
  '32-team-bracket': 'free-tools/engagement/32-team-bracket',
  '64-team-bracket': 'free-tools/engagement/64-team-bracket',
  'blank-bracket': 'free-tools/engagement/blank-bracket',
  'single-elimination-bracket': 'free-tools/engagement/single-elimination-bracket',
  'double-elimination-bracket': 'free-tools/engagement/double-elimination-bracket',
  'triple-elimination-bracket': 'free-tools/engagement/triple-elimination-bracket',
  'consolation-bracket': 'free-tools/engagement/consolation-bracket',
  'march-madness-bracket': 'free-tools/engagement/march-madness-bracket',
  'nfl-playoff-bracket': 'free-tools/engagement/nfl-playoff-bracket',
  'college-football-playoff-bracket': 'free-tools/engagement/college-football-playoff-bracket',
  'alignment-chart-maker': 'free-tools/engagement/alignment-chart-maker',
  // Potluck sign-up sheet, 2026-10-14. Printed and pasted into group chats, so it carries the credit.
  'potluck-sign-up-sheet': 'free-tools/engagement/potluck-sign-up-sheet',
  // Bingo, 2026-10-12. Printed cards carry it too: print is the main output.
  'bingo-card-generator': 'free-tools/engagement/bingo-card-generator',
  'bingo-card-generator-christmas': 'free-tools/engagement/bingo-card-generator/christmas',
  'bingo-card-generator-thanksgiving': 'free-tools/engagement/bingo-card-generator/thanksgiving',
  'bingo-card-generator-new-years': 'free-tools/engagement/bingo-card-generator/new-years',
  'bingo-card-generator-hanukkah': 'free-tools/engagement/bingo-card-generator/hanukkah',
  'bingo-card-generator-valentines': 'free-tools/engagement/bingo-card-generator/valentines',
  // Holiday trivia, 2026-10-12. Printed quizzes, the round image and the score card.
  'holiday-trivia': 'free-tools/engagement/holiday-trivia',
  'holiday-trivia-thanksgiving': 'free-tools/engagement/holiday-trivia/thanksgiving',
  'holiday-trivia-christmas': 'free-tools/engagement/holiday-trivia/christmas',
  'holiday-trivia-hanukkah': 'free-tools/engagement/holiday-trivia/hanukkah',
  // Holiday tier list presets, 2026-10-14. Same Tier List Maker, credit points at the page.
  'tier-list-maker-thanksgiving-food': 'free-tools/engagement/tier-list-maker/thanksgiving-food',
  'tier-list-maker-christmas-movies': 'free-tools/engagement/tier-list-maker/christmas-movies',
} as const;

export type BrandMarkTool = keyof typeof BRAND_MARK_PATHS;

export const brandMarkText = (tool: BrandMarkTool) =>
  `Made with commoninja.com/${BRAND_MARK_PATHS[tool]}`;

/**
 * The Common Ninja app icon, exactly as the site header draws it
 * (public/assets/cn-logo-light.svg and cn-logo-dark.svg): a rounded dark square,
 * the white mask band across it, two dark eye strokes. On a dark background the
 * header adds a thin white outline so the square does not vanish, and so do we.
 */
const ICON_SIZE = 29;
const ICON_RADIUS = 3.8;
const ICON_SQUARE = '#02040F';
const ICON_BAND = 'M0 22.8223L0 10.2464L29.0002 6.17065V18.7466L0 22.8223Z';
const ICON_EYES = ['M22.5886 11.292L17.7865 16.0723L23.8254 15.2236', 'M5.79669 13.6526L11.7304 16.9241L5.69151 17.7728'];
const ICON_EYE_W = 1.0206;

/**
 * Ink for the credit text, chosen from the background it sits on: near-black on
 * a light surface, white on a dark one (Sergei, 2026-10-08). Relative
 * luminance, so it holds for every theme and any colour a future tool passes.
 * Unparseable input counts as light.
 */
const parseRgb = (c: string): [number, number, number] | null => {
  const v = c.trim().toLowerCase();
  const hex = v.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].split('').map((x) => x + x).join('') : hex[1];
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
  }
  const rgb = v.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/);
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])];
  return null;
};

export const isDarkBackground = (background: string) => {
  const rgb = parseRgb(background);
  if (!rgb) return false;
  const [r, g, b] = rgb.map((x) => {
    const c = x / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.4;
};

const inkFor = (background: string) => (isDarkBackground(background) ? '#ffffff' : ICON_SQUARE);

const BrandMarkIcon = ({ onDark }: { onDark: boolean }) => (
  <svg className="bm-icon" viewBox={`0 0 ${ICON_SIZE} ${ICON_SIZE}`} aria-hidden="true">
    {/* No clip path needed: the band never reaches the rounded corners, and an
        id-based clip would be duplicated by the preview and the export copy. */}
    <g>
      <rect width={ICON_SIZE} height={ICON_SIZE} rx={ICON_RADIUS} fill={ICON_SQUARE} />
      <path d={ICON_BAND} fill="#ffffff" />
      {ICON_EYES.map((d) => (
        <path
          key={d}
          d={d}
          stroke={ICON_SQUARE}
          strokeWidth={ICON_EYE_W}
          strokeLinecap="round"
          fill="none"
        />
      ))}
    </g>
    {onDark && (
      <rect
        x={0.4}
        y={0.4}
        width={ICON_SIZE - 0.8}
        height={ICON_SIZE - 0.8}
        rx={ICON_RADIUS}
        fill="none"
        stroke="#ffffff"
        strokeWidth={0.8}
      />
    )}
  </svg>
);

const STORAGE_KEY = (tool: BrandMarkTool) => `cn_brand_mark_off:${tool}`;

/**
 * `/api/v1/user` answers `{ success: false, message: "User is not logged in" }`
 * for a visitor without a session and the user record otherwise. Verified on
 * www 2026-10-08. `planFeatures` cannot be used: it returns free-plan features
 * to logged-out visitors too.
 *
 * Locally there is no session and no API, so this always reads "logged out".
 * `?cn_mark_debug=loggedin` fakes a session in development only.
 */
const fetchLoggedIn = async (): Promise<boolean> => {
  try {
    if (
      process.env.NODE_ENV === 'development' &&
      new URLSearchParams(window.location.search).get('cn_mark_debug') === 'loggedin'
    ) {
      return true;
    }
    const res = await fetch('/api/v1/user', { credentials: 'include' });
    if (!res.ok) return false;
    const json = await res.json();
    return !!json?.success;
  } catch {
    return false;
  }
};

const safeGet = (key: string) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSet = (key: string, value: string | null) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* private mode: the choice just will not be remembered */
  }
};

export const useBrandMark = (tool?: BrandMarkTool) => {
  const enabled = !!tool;
  const [markOn, setMarkOn] = useState(enabled);
  const [loggedIn, setLoggedIn] = useState(false);
  // Set when the visitor went to sign up from this tab, so the mark comes off
  // by itself when they come back with a session.
  const pendingSignup = useRef(false);

  useEffect(() => {
    if (!tool) return;
    let cancelled = false;

    fetchLoggedIn().then((isIn) => {
      if (cancelled) return;
      setLoggedIn(isIn);
      // A remembered "off" only counts for a visitor who still has an account
      // session, otherwise clearing a cookie would be a free bypass that skips
      // the sign-up the option is offered for.
      if (isIn && safeGet(STORAGE_KEY(tool)) === '1') setMarkOn(false);
    });

    const recheck = () => {
      if (document.visibilityState !== 'visible' || !pendingSignup.current) return;
      fetchLoggedIn().then((isIn) => {
        if (cancelled || !isIn) return;
        pendingSignup.current = false;
        setLoggedIn(true);
        setMarkOn(false);
        safeSet(STORAGE_KEY(tool), '1');
        trackEvent('brand_mark_removed', { tool, via: 'signup' });
      });
    };

    document.addEventListener('visibilitychange', recheck);
    window.addEventListener('focus', recheck);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', recheck);
      window.removeEventListener('focus', recheck);
    };
  }, [tool]);

  const removeMark = useCallback(() => {
    if (!tool) return;
    if (loggedIn) {
      setMarkOn(false);
      safeSet(STORAGE_KEY(tool), '1');
      trackEvent('brand_mark_removed', { tool, via: 'toggle' });
      return;
    }
    // A new tab, so the board or chat they built stays right here. The sign-up
    // page sends them back to the tool, and this tab notices the new session
    // when it is looked at again.
    pendingSignup.current = true;
    trackEvent('brand_mark_remove_click', { tool });
    const back = `${window.location.pathname}`;
    window.open(`/signup?redirectUrl=${encodeURIComponent(back)}`, '_blank', 'noopener');
  }, [tool, loggedIn]);

  const restoreMark = useCallback(() => {
    if (!tool) return;
    setMarkOn(true);
    safeSet(STORAGE_KEY(tool), null);
    trackEvent('brand_mark_restored', { tool });
  }, [tool]);

  return { enabled, markOn: enabled && markOn, loggedIn, removeMark, restoreMark };
};

interface IStripProps {
  tool: BrandMarkTool;
  /** The surface it sits on. The ink colour is derived from it. */
  background: string;
}

/** The credit as DOM, for tools that export with html-to-image. */
export const BrandMarkStrip = ({ tool, background }: IStripProps) => (
  <StyledBrandMarkStrip $bg={background} $fg={inkFor(background)}>
    {/* A full tool address is wider than a phone. If it has to wrap, it wraps
        after a slash, never in the middle of a word. */}
    <span className="bm-seg">
      <BrandMarkIcon onDark={isDarkBackground(background)} />
      Made with commoninja.com/
    </span>
    {BRAND_MARK_PATHS[tool].split('/').map((part, i, all) => (
      <React.Fragment key={i}>
        <wbr />
        <span className="bm-seg">
          {part}
          {i < all.length - 1 ? '/' : ''}
        </span>
      </React.Fragment>
    ))}
  </StyledBrandMarkStrip>
);

/**
 * The credit as a bare text line, for canvas tools whose on-page board stands
 * in for the export. Mirrors drawBrandMark so the preview tells the truth.
 */
export const BrandMarkLine = ({ tool, background }: { tool: BrandMarkTool; background: string }) => (
  <StyledBrandMarkLine $fg={inkFor(background)}>
    <BrandMarkIcon onDark={isDarkBackground(background)} />
    {brandMarkText(tool)}
  </StyledBrandMarkLine>
);

/** Height in px that a canvas export adds below the board for the credit. */
export const BRAND_MARK_CANVAS_H = 34;

/** The credit as canvas pixels, bottom left of a band of BRAND_MARK_CANVAS_H. */
export const drawBrandMark = (
  ctx: CanvasRenderingContext2D,
  tool: BrandMarkTool,
  opts: { x: number; bandTop: number; background: string }
) => {
  const midY = opts.bandTop + BRAND_MARK_CANVAS_H / 2;
  const onDark = isDarkBackground(opts.background);
  const iconPx = 18;
  const scale = iconPx / ICON_SIZE;

  ctx.save();
  ctx.translate(opts.x, midY - iconPx / 2);
  ctx.scale(scale, scale);
  const square = new Path2D();
  square.roundRect(0, 0, ICON_SIZE, ICON_SIZE, ICON_RADIUS);
  ctx.save();
  ctx.clip(square);
  ctx.fillStyle = ICON_SQUARE;
  ctx.fillRect(0, 0, ICON_SIZE, ICON_SIZE);
  ctx.fillStyle = '#ffffff';
  ctx.fill(new Path2D(ICON_BAND));
  ctx.strokeStyle = ICON_SQUARE;
  ctx.lineWidth = ICON_EYE_W;
  ctx.lineCap = 'round';
  ICON_EYES.forEach((d) => ctx.stroke(new Path2D(d)));
  ctx.restore();
  if (onDark) {
    const outline = new Path2D();
    outline.roundRect(0.4, 0.4, ICON_SIZE - 0.8, ICON_SIZE - 0.8, ICON_RADIUS);
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 0.8;
    ctx.stroke(outline);
  }
  ctx.restore();

  ctx.save();
  ctx.fillStyle = inkFor(opts.background);
  ctx.font = "500 15px Poppins, 'Open Sans', sans-serif";
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(brandMarkText(tool), opts.x + iconPx + 7, midY);
  ctx.restore();
};

interface IControlProps {
  mark: ReturnType<typeof useBrandMark>;
}

/** The one line under the download button that says the credit is there. */
export const BrandMarkControl = ({ mark }: IControlProps) => {
  if (!mark.enabled) return null;

  if (!mark.markOn) {
    return (
      <StyledBrandMarkControl>
        <span>Credit removed from your image.</span>
        <button type="button" onClick={mark.restoreMark}>
          Put it back
        </button>
      </StyledBrandMarkControl>
    );
  }

  return (
    <StyledBrandMarkControl>
      <span>Your image has a small commoninja.com credit.</span>
      <button type="button" onClick={mark.removeMark}>
        {mark.loggedIn ? 'Remove it' : 'Remove it free with an account'}
      </button>
    </StyledBrandMarkControl>
  );
};
